import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  DestroyRef,
  inject,
  ChangeDetectorRef,
  Optional,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule, RouterLink } from '@angular/router';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import { CourseEditorService } from 'core/services/course-editor.service';
import { CourseViewerService } from 'core/services/course-viewer.service';
import {
  Course,
  Lesson,
  Question,
  Module,
} from 'core/models/course-content.model';

@Component({
  selector: 'app-course-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RouterLink,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
  ],
  templateUrl: './course-sidebar.html',
})
export class CourseSidebar implements OnInit {
  @Input() variant!: 'course-editor' | 'course-viewer';
  @Input() isOpen = true;
  @Input() course: Course | null = null;
  @Output() closeSidebar = new EventEmitter<void>();
  @Output() lessonClick = new EventEmitter<string>();
  @Output() moduleClick = new EventEmitter<string>();
  @Output() questionClick = new EventEmitter<string>();
  @Output() addModule = new EventEmitter<void>();
  @Output() addLesson = new EventEmitter<string>();
  @Output() editLesson = new EventEmitter<{
    moduleId: string;
    lessonId: string;
  }>();
  @Output() deleteLesson = new EventEmitter<{
    moduleId: string;
    lessonId: string;
  }>();

  protected readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // Services condicionais
  private courseEditorService: CourseEditorService | null = null;
  private courseViewerService: CourseViewerService | null = null;

  // Propriedades comuns
  protected menuState: Record<string, boolean> = {};

  // Propriedades específicas do admin
  // (menuState já está acima)

  // Propriedades específicas do course-editor e course-viewer
  protected currentLessonId: string | null = null;
  protected currentQuestionId: string | null = null;

  constructor(
    @Optional() courseEditorService: CourseEditorService,
    @Optional() courseViewerService: CourseViewerService,
  ) {
    this.courseEditorService = courseEditorService;
    this.courseViewerService = courseViewerService;
  }

  ngOnInit(): void {
    if (this.variant === 'course-editor' && this.courseEditorService) {
      this.loadCourseEditorData();
      this.setupCourseEditorSubscriptions();
    } else if (this.variant === 'course-viewer' && this.courseViewerService) {
      this.loadCourseViewerData();
      this.setupCourseViewerSubscriptions();
    }
  }

  // ============================================================================
  // MÉTODOS COMUNS
  // ============================================================================

  protected handleCloseSidebar(): void {
    this.closeSidebar.emit();
  }

  // ============================================================================
  // MÉTODOS ESPECÍFICOS DO COURSE-EDITOR E COURSE-VIEWER
  // ============================================================================

  protected toggleMenu(moduleId: string): void {
    const isCurrentlyOpen = this.menuState[moduleId];
    this.closeAllMenusExcept(moduleId);
    this.menuState[moduleId] = !isCurrentlyOpen;

    if (!isCurrentlyOpen && this.course) {
      const module = this.course.modules.find((m) => m.id === moduleId);

      if (module) {
        if (
          module.contentType === 'lessons' &&
          module.lessons &&
          module.lessons.length > 0
        ) {
          const sortedLessons = [...module.lessons].sort(
            (a, b) => a.order - b.order,
          );
          if (sortedLessons[0]) {
            this.lessonClick.emit(sortedLessons[0].id);
            return;
          }
        }

        if (
          this.variant === 'course-editor' &&
          module.contentType === 'evaluations' &&
          module.questions &&
          module.questions.length > 0
        ) {
          const sortedQuestions = [...module.questions].sort(
            (a, b) => a.order - b.order,
          );
          if (sortedQuestions[0]) {
            this.questionClick.emit(sortedQuestions[0].id);
            return;
          }
        }

        if (this.variant === 'course-editor') {
          this.moduleClick.emit(moduleId);
        }
      }
    }
  }

  protected isMenuOpen(moduleId: string): boolean {
    return !!this.menuState[moduleId];
  }

  private closeAllMenusExcept(exceptModuleId?: string): void {
    if (!this.course) {
      return;
    }

    this.course.modules.forEach((module) => {
      if (module.id !== exceptModuleId) {
        this.menuState[module.id] = false;
      }
    });
  }

  // ============================================================================
  // MÉTODOS ESPECÍFICOS DO COURSE-EDITOR
  // ============================================================================

  private setupCourseEditorSubscriptions(): void {
    if (!this.courseEditorService || this.variant !== 'course-editor') return;

    this.courseEditorService.lessonChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lessonId) => {
        this.currentLessonId = lessonId;
        if (lessonId) {
          this.currentQuestionId = null;
          this.openModuleMenuForLesson(lessonId);
        }
        this.cdr.markForCheck();
      });

    this.courseEditorService.questionChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((questionId) => {
        this.currentQuestionId = questionId;
        if (questionId) {
          this.currentLessonId = null;
          this.openModuleMenuForQuestion(questionId);
        }
        this.cdr.markForCheck();
      });

    this.courseEditorService.course$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((course) => {
        this.course = course;
        if (this.courseEditorService) {
          const currentLesson = this.courseEditorService.getCurrentLesson();
          const currentQuestion = this.courseEditorService.getCurrentQuestion();
          this.currentLessonId = currentLesson?.id || null;
          this.currentQuestionId = currentQuestion?.id || null;
        }
        this.cdr.markForCheck();
      });
  }

  private loadCourseEditorData(): void {
    if (!this.courseEditorService || this.variant !== 'course-editor') return;

    this.course = this.courseEditorService.getCourse();
    const currentLesson = this.courseEditorService.getCurrentLesson();
    const currentQuestion = this.courseEditorService.getCurrentQuestion();
    this.currentLessonId = currentLesson?.id || null;
    this.currentQuestionId = currentQuestion?.id || null;

    if (this.course) {
      this.closeAllMenusExcept();
      if (this.currentLessonId) {
        this.openModuleMenuForLesson(this.currentLessonId);
      } else if (this.currentQuestionId) {
        this.openModuleMenuForQuestion(this.currentQuestionId);
      } else if (this.course.modules.length > 0) {
        const firstModuleId = this.course.modules[0].id;
        this.closeAllMenusExcept(firstModuleId);
        this.menuState[firstModuleId] = true;
      }
    }
    this.cdr.markForCheck();
  }

  private openModuleMenuForLesson(lessonId: string | null): void {
    if (!this.course || !lessonId) return;

    const module = this.course.modules.find(
      (m) =>
        m.contentType === 'lessons' &&
        m.lessons &&
        m.lessons.some((l) => l.id === lessonId),
    );

    if (module) {
      this.closeAllMenusExcept(module.id);
      this.menuState[module.id] = true;
    }
  }

  private openModuleMenuForQuestion(questionId: string | null): void {
    if (!this.course || !questionId || this.variant !== 'course-editor') return;

    const module = this.course.modules.find(
      (m) =>
        m.contentType === 'evaluations' &&
        m.questions &&
        m.questions.some((q) => q.id === questionId),
    );

    if (module) {
      this.closeAllMenusExcept(module.id);
      this.menuState[module.id] = true;
    }
  }

  protected onAddModule(event: Event): void {
    if (this.variant !== 'course-editor') return;
    event.preventDefault();
    event.stopPropagation();
    this.addModule.emit();
  }

  protected onAddLesson(moduleId: string, event: Event): void {
    if (this.variant !== 'course-editor') return;
    event.preventDefault();
    event.stopPropagation();
    this.addLesson.emit(moduleId);
  }

  protected onEditLesson(
    moduleId: string,
    lessonId: string,
    event: Event,
  ): void {
    if (this.variant !== 'course-editor') return;
    event.preventDefault();
    event.stopPropagation();
    this.editLesson.emit({ moduleId, lessonId });
  }

  protected onDeleteLesson(
    moduleId: string,
    lessonId: string,
    event: Event,
  ): void {
    if (this.variant !== 'course-editor') return;
    event.preventDefault();
    event.stopPropagation();
    if (confirm('Tem certeza que deseja excluir esta lição?')) {
      this.deleteLesson.emit({ moduleId, lessonId });
    }
  }

  protected onModuleDrop(event: CdkDragDrop<Module[]>): void {
    if (
      this.variant !== 'course-editor' ||
      !this.course ||
      !this.courseEditorService
    )
      return;

    const modules = [...this.course.modules];
    moveItemInArray(modules, event.previousIndex, event.currentIndex);

    const newOrder = modules.map((m) => m.id);
    this.courseEditorService.reorderModules(newOrder);
  }

  protected onLessonDrop(event: CdkDragDrop<Lesson[]>, moduleId: string): void {
    if (
      this.variant !== 'course-editor' ||
      !this.course ||
      !this.courseEditorService
    )
      return;

    const module = this.course.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'lessons' || !module.lessons) {
      return;
    }

    const lessons = [...module.lessons];
    moveItemInArray(lessons, event.previousIndex, event.currentIndex);

    const newOrder = lessons.map((l) => l.id);
    this.courseEditorService.reorderLessonsInModule(moduleId, newOrder);
  }

  protected onQuestionDrop(
    event: CdkDragDrop<Question[]>,
    moduleId: string,
  ): void {
    if (
      this.variant !== 'course-editor' ||
      !this.course ||
      !this.courseEditorService
    )
      return;

    const module = this.course.modules.find((m) => m.id === moduleId);
    if (!module || module.contentType !== 'evaluations' || !module.questions) {
      return;
    }

    const questions = [...module.questions];
    moveItemInArray(questions, event.previousIndex, event.currentIndex);

    const newOrder = questions.map((q) => q.id);
    this.courseEditorService.reorderQuestionsInModule(moduleId, newOrder);
  }

  protected isCurrentLesson(lessonId: string): boolean {
    return this.currentLessonId === lessonId;
  }

  protected isCurrentQuestion(questionId: string): boolean {
    return this.currentQuestionId === questionId;
  }

  protected getLessonIcon(lesson: Lesson): string {
    if (this.variant === 'course-viewer') {
      if (this.isCurrentLesson(lesson.id)) {
        return 'bi-play-circle-fill text-blue-500';
      }
      return lesson.completed
        ? 'bi-check-circle-fill text-green-500'
        : 'bi-circle text-gray-400';
    } else if (this.variant === 'course-editor') {
      const isActive = this.isCurrentLesson(lesson.id);
      return this.getLessonTypeIcon(lesson.type, isActive);
    }
    return '';
  }

  protected getLessonTypeIcon(type: Lesson['type'], isActive = false): string {
    const colorClass = isActive
      ? 'text-blue-500'
      : 'text-gray-400 dark:text-neutral-500';

    switch (type) {
      case 'video':
        return isActive
          ? `bi-play-circle-fill ${colorClass}`
          : `bi-play-circle ${colorClass}`;
      case 'pdf':
        return `bi-file-pdf ${colorClass}`;
      case 'evaluation':
        return isActive
          ? `bi-clipboard-check-fill ${colorClass}`
          : `bi-clipboard-check ${colorClass}`;
      default:
        return `bi-file ${colorClass}`;
    }
  }

  protected getLessonDisplayTitle(lesson: Lesson): string {
    return lesson.title;
  }

  protected getQuestionDisplayTitle(question: Question): string {
    const text = question.text;
    return text.length > 50 ? text.substring(0, 50) + '...' : text;
  }

  protected getQuestionIcon(questionId: string): string {
    if (this.isCurrentQuestion(questionId)) {
      return 'bi-clipboard-check-fill text-blue-500';
    }
    return 'bi-clipboard-check text-gray-400 dark:text-neutral-500';
  }

  protected getSortedQuestions(questions: Question[] | undefined): Question[] {
    if (!questions) {
      return [];
    }
    return [...questions].sort((a, b) => a.order - b.order);
  }

  protected onModuleClick(moduleId: string): void {
    if (this.variant === 'course-editor') {
      this.moduleClick.emit(moduleId);
    }
  }

  protected onQuestionClick(
    moduleId: string,
    questionId: string,
    event: Event,
  ): void {
    if (this.variant !== 'course-editor') return;
    event.preventDefault();
    event.stopPropagation();
    this.questionClick.emit(questionId);
  }

  protected onLessonClick(lessonId: string, event: Event): void {
    event.preventDefault();
    if (this.variant === 'course-viewer') {
      this.currentLessonId = lessonId;
    }
    this.lessonClick.emit(lessonId);
  }

  protected isLessonCompleted(lesson: Lesson): boolean {
    return lesson.completed || false;
  }

  // ============================================================================
  // MÉTODOS ESPECÍFICOS DO COURSE-VIEWER
  // ============================================================================

  private setupCourseViewerSubscriptions(): void {
    if (!this.courseViewerService || this.variant !== 'course-viewer') return;

    this.courseViewerService.lessonChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lessonId) => {
        this.currentLessonId = lessonId;
        if (this.courseViewerService) {
          this.course = this.courseViewerService.getCourseForSidebar();
        }
        this.openModuleMenuForLesson(lessonId);
      });
  }

  private loadCourseViewerData(): void {
    if (!this.courseViewerService || this.variant !== 'course-viewer') return;

    this.course = this.courseViewerService.getCourseForSidebar();
    const currentLesson = this.courseViewerService.getCurrentLesson();
    this.currentLessonId = currentLesson?.id || null;

    if (this.course) {
      this.closeAllMenusExcept();
      if (this.currentLessonId) {
        this.openModuleMenuForLesson(this.currentLessonId);
      } else {
        if (this.course.modules.length > 0) {
          const firstModuleId = this.course.modules[0].id;
          this.closeAllMenusExcept(firstModuleId);
          this.menuState[firstModuleId] = true;
        }
      }
    }
  }

  protected getSidebarId(): string {
    return this.variant === 'course-editor'
      ? 'course-editor-sidebar'
      : 'course-viewer-sidebar';
  }
}
